import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Anime, getRelatedAnime, formatViews } from "@/mocks/anime";

type Props = {
  anime: Anime;
  isFav: boolean;
  onToggleFav: () => void;
};

export default function DetailHero({ anime, isFav, onToggleFav }: Props) {
  const navigate = useNavigate();
  const [toast, setToast] = useState("");
  const [showPlayerTip, setShowPlayerTip] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，可粘贴分享给朋友");
    } catch {
      showToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  const related = getRelatedAnime(anime, 4);

  return (
    <section className="w-full bg-primary-950 relative">
      <div className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] lg:grid-cols-[280px_1fr] gap-6 md:gap-8">
          <div className="w-full max-w-[240px] md:max-w-none">
            <div className="w-full aspect-[5/7] rounded-lg overflow-hidden bg-primary-900">
              <img
                src={anime.cover}
                alt={`${anime.title}大幅封面`}
                title={`${anime.title} - 妖精动漫作品详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={onToggleFav}
                className={`h-10 rounded-md text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer ${
                  isFav
                    ? "bg-accent-500 text-primary-950 hover:bg-accent-600"
                    : "bg-primary-900 text-background-50 hover:bg-primary-800"
                }`}
              >
                <i className={isFav ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {isFav ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={share}
                className="h-10 rounded-md text-[13px] font-semibold flex items-center justify-center gap-1.5 bg-primary-900 text-background-50 hover:bg-primary-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>

          <div className="min-w-0 flex flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-accent-500 text-primary-950 text-[11px] font-bold">{anime.status}</span>
              <span className="px-2 py-0.5 rounded bg-primary-900 text-primary-100 text-[11px] font-medium">{anime.region}</span>
              <span className="px-2 py-0.5 rounded bg-primary-900 text-primary-100 text-[11px] font-medium">{anime.type}</span>
              <span className="px-2 py-0.5 rounded bg-primary-900 text-primary-100 text-[11px] font-medium">{anime.year}年</span>
            </div>

            <h1 className="mt-3 text-2xl md:text-4xl font-black tracking-tight text-background-50 leading-tight">
              {anime.title}
            </h1>
            <p className="mt-1 text-[11px] md:text-xs tracking-[0.2em] uppercase text-accent-300 font-semibold">
              {anime.originalTitle}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-black text-accent-400">{anime.rating.toFixed(1)}</span>
                <span className="text-[12px] text-primary-300">/ 10 评分</span>
              </div>
              <div className="h-8 w-px bg-primary-800 hidden sm:block" />
              <div className="text-[12px] text-primary-200 space-y-1">
                <p className="flex items-center gap-1.5">
                  <i className="ri-play-circle-line" /> {formatViews(anime.views)} 播放
                </p>
                <p className="flex items-center gap-1.5">
                  <i className="ri-list-check-2" /> {anime.status === "已完结" ? `全 ${anime.episodes} 话` : `更新至第 ${anime.chapterList.length + 6} 话`}
                </p>
              </div>
              <div className="h-8 w-px bg-primary-800 hidden sm:block" />
              <div className="text-[12px] text-primary-200 space-y-1">
                <p className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" /> {anime.serialDay}
                </p>
                <p className="flex items-center gap-1.5">
                  <i className="ri-time-line" /> 最近更新 {anime.updatedAt}
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-1.5">
              {anime.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-primary-900 text-primary-100 text-[11px] font-medium">
                  #{tag}
                </span>
              ))}
            </div>

            <p className="mt-5 text-[13px] md:text-[14px] leading-7 text-primary-100 max-w-[720px]">{anime.summary}</p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShowPlayerTip(true)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-[14px] font-bold transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-fill text-lg leading-none" />
                播放演示
              </button>
              <button
                type="button"
                onClick={() => navigate(`/category/${anime.region === "国漫" ? "guoman" : anime.region === "日漫" ? "riman" : "hanman"}`)}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-primary-700 text-background-100 text-[13px] font-medium hover:bg-primary-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line" />
                同分类作品
              </button>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-[720px]">
              {[
                { label: "制作", value: anime.studio },
                { label: "导演", value: anime.director },
                { label: "类型", value: anime.type },
                { label: "地区", value: anime.region },
              ].map((item) => (
                <div key={item.label} className="rounded-md bg-primary-900 px-3 py-2">
                  <p className="text-[10px] text-primary-400 tracking-wide">{item.label}</p>
                  <p className="mt-0.5 text-[12px] font-semibold text-background-100 truncate">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-10 pt-6 border-t border-primary-900">
            <p className="text-[11px] font-bold tracking-[0.2em] text-accent-400 mb-3">相关推荐</p>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="w-[150px] shrink-0 group cursor-pointer"
                >
                  <div className="w-full aspect-[5/7] rounded-md overflow-hidden">
                    <img
                      src={item.cover}
                      alt={`${item.title}推荐封面`}
                      title={`${item.title} - 相关推荐`}
                      className="w-full h-full object-cover object-top group-hover:opacity-85 transition-opacity"
                    />
                  </div>
                  <p className="mt-2 text-[12px] font-medium text-background-100 line-clamp-1">{item.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {showPlayerTip && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-primary-950/60" onClick={() => setShowPlayerTip(false)} />
          <div className="relative w-full max-w-[400px] rounded-lg bg-background-50 p-6 text-center animate-fade-up">
            <span className="w-14 h-14 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-movie-2-line text-2xl" />
            </span>
            <h3 className="mt-4 text-[16px] font-bold text-foreground-950">播放演示</h3>
            <p className="mt-2 text-[13px] leading-6 text-foreground-600">
              本站为内容展示演示站点，暂未接入真实视频资源。你可以先查看作品简介与章节列表，感受完整的浏览体验。
            </p>
            <button
              type="button"
              onClick={() => setShowPlayerTip(false)}
              className="mt-5 w-full h-10 rounded-md bg-primary-900 hover:bg-primary-800 text-background-50 text-[13px] font-semibold transition-colors cursor-pointer"
            >
              知道了
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-8 z-[70] px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-[13px] font-medium animate-fade-up whitespace-nowrap">
          {toast}
        </div>
      )}
    </section>
  );
}